import {
  Component,
  OnInit,
  Inject,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  Observable,
  combineLatest,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { TableComponent } from '../../../shared/components/ui/table/table.component';
import { SearchBarComponent } from '../../../shared/components/ui/search-bar/search-bar.component';
import { FilterTabsComponent } from '../../../shared/components/ui/filter-tabs/filter-tabs.component';
import { SortDropdownComponent } from '../../../shared/components/ui/sort-dropdown/sort-dropdown.component';
import { PaginationComponent } from '../../../shared/components/ui/pagination/pagination.component';
import { LettersService } from '../../../shared/services/letters.service';
import { TableColumn } from '../../../shared/models/table.model';
import {
  FilterTab,
  SortOption,
  PaginationInfo,
  LetterData,
  LettersResponse,
} from '../../../shared/models/letters.model';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../../../core/services/auth.service';
import { UserProfile, UserRole } from '../../../shared/models/auth.model';
import { DocumentViewerModalComponent } from '../../../shared/components/ui/document-viewer-modal/document-viewer-modal.component';
import { DocumentAttachment } from '../../../shared/models/routing-slip.model';
import { CardListComponent } from '../../../shared/components/ui/card-list/card-list.component';
import { LoaderComponent } from '../../../shared/components/ui/loader/loader.component';
import { AuditModalComponent } from '../../../shared/components/ui/audit-modal/audit-modal.component';
import { AuditsData } from '../../../shared/models/letters.model';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-letters-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TableComponent,
    SearchBarComponent,
    FilterTabsComponent,
    SortDropdownComponent,
    PaginationComponent,
    ButtonModule,
    DocumentViewerModalComponent,
    CardListComponent,
    LoaderComponent,
    AuditModalComponent,
    ToastModule,
  ],
  providers: [LettersService, MessageService],
  templateUrl: './letters-list.component.html',
  styleUrl: './letters-list.component.scss',
})
export class LettersListComponent implements OnInit, OnDestroy {
  // Filter tabs
  filterTabs: FilterTab[] = [];
  activeFilterTab: string = 'all';

  // Sort options
  sortOptions: SortOption[] = [];
  selectedSortOption: string = 'newest'; // This should match one of the option values

  // Search
  searchTerm: string = '';

  // Debounce subject for search
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Pagination
  paginationInfo!: PaginationInfo;

  // Table columns
  columns: TableColumn[] = [];

  // Table data
  data: LetterData[] = [];
  allData: LetterData[] = []; // All data for pagination

  // Role-based access control
  isManager: boolean = false;

  // Document viewer state
  showDocumentViewer: boolean = false;
  currentDocument: DocumentAttachment | null = null;
  loading: boolean = false;
  pageLoading: boolean = true; // Full page loading state
  documentLoading: boolean = false; // Document loading state
  userProfile: UserProfile | null = null;

  // Audit log state
  auditData: AuditsData[] = [];
  selectedRow: any;
  auditDialogVisible: boolean = false;
  auditLoading: boolean = false;

  constructor(
    private lettersService: LettersService,
    private router: Router,
    private authService: AuthService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.checkUserRole();
    this.setupSearchDebounce();

    // Add a small delay to ensure loader is visible
    setTimeout(() => {
      this.loadLettersData();
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchDebounce(): void {
    this.searchSubject
      .pipe(
        debounceTime(1000), // 1000ms delay - 1 second for user to finish typing
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((searchTerm: string) => {
        this.searchTerm = searchTerm;
        this.paginationInfo.currentPage = 1;
        this.loadPageData();
      });
  }

  // private checkUserRole(): void {
  //   this.isManager = this.authService.hasRole(UserRole.MANAGER);
  // }
  private checkUserRole(): void {
    const currentUser = this.authService.getCurrentUser();

    // Set userProfile from authenticated user
    if (currentUser) {
      this.userProfile = {
        id: currentUser.email,
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        avatar: 'assets/images/logo.png',
      };
    }

    this.isManager = this.authService.hasRole(UserRole.MANAGER);
  }
  isMobile = window.innerWidth < 640;
  private loadLettersData(): void {
    // Initialize filter tabs with empty counts (will be updated when data loads)
    this.filterTabs = [
      { id: 'all', label: 'All', count: 0 },
      { id: 'pending', label: 'Pending', count: 0 },
      { id: 'responded', label: 'Responded', count: 0 },
      { id: 'archive', label: 'Archive', count: 0 },
    ];

    // Load sort options (from local JSON for UI)
    this.lettersService.getSortOptions().subscribe((options) => {
      this.sortOptions = options;
    });

    // Load pagination info (from local JSON for initial setup)
    this.lettersService.getPaginationInfo().subscribe((info) => {
      this.paginationInfo = info;
      // After pagination info is loaded, load the actual data from API
      this.loadPageData();
    });

    // Load table columns (from local JSON for UI)
    this.lettersService.getTableColumns().subscribe((columns) => {
      this.columns = columns;
    });
  }

  private loadPageData(): void {
    // Set loading state
    this.loading = true;

    // Use the new service method with filters
    this.lettersService
      .getLettersWithFilters({
        filter: this.activeFilterTab,
        search: this.searchTerm,
        sortBy: this.selectedSortOption,
        page: this.paginationInfo?.currentPage || 1,
        perPage: this.paginationInfo?.itemsPerPage || 10,
        showDeleted: this.activeFilterTab === 'archive',
      })
      .subscribe({
        next: (response: LettersResponse) => {
          this.data = response.data;
          this.loading = false;

          // Ensure loader shows for minimum duration
          setTimeout(() => {
            this.pageLoading = false;
          }, 500);

          // Update pagination info
          if (this.paginationInfo) {
            this.paginationInfo.totalItems = response.total;
            this.paginationInfo.currentPage = response.page;
            this.paginationInfo.totalPages = Math.ceil(
              response.total / response.perPage
            );
            this.paginationInfo.startItem =
              (response.page - 1) * response.perPage + 1;
            this.paginationInfo.endItem = Math.min(
              response.page * response.perPage,
              response.total
            );
          }

          // Update filter tabs with real counts
          this.updateFilterTabs();
        },
        error: (error) => {
          this.loading = false;
          this.pageLoading = false; // Hide page loader even on error
          // You could show an error message to the user here
        },
      });
  }

  // Event handlers
  onFilterTabChange(tabId: string) {
    this.activeFilterTab = tabId;
    if (this.paginationInfo) {
      this.paginationInfo.currentPage = 1;
    }
    this.loadPageData();
  }

  onSortChange(sortValue: string) {
    this.selectedSortOption = sortValue;
    if (this.paginationInfo) {
      this.paginationInfo.currentPage = 1;
    }
    this.loadPageData();
  }

  onSearchChange(searchTerm: string) {
    this.searchSubject.next(searchTerm);
  }

  clearSearch(): void {
    this.onSearchChange('');
  }

  onPageChange(page: number) {
    if (this.paginationInfo) {
      this.paginationInfo.currentPage = page;
    }
    this.loadPageData();
  }
  ngOnChanges() {
  }

  private updateFilterTabs(): void {
    // Get counts for each filter from separate API calls
    const allCount$ = this.lettersService
      .getLettersWithFilters({ filter: 'all', page: 1, perPage: 1 })
      .pipe(map((response) => response.total));
    const pendingCount$ = this.lettersService
      .getLettersWithFilters({ filter: 'pending', page: 1, perPage: 1 })
      .pipe(map((response) => response.total));
    const respondedCount$ = this.lettersService
      .getLettersWithFilters({ filter: 'responded', page: 1, perPage: 1 })
      .pipe(map((response) => response.total));
    const archiveCount$ = this.getArchiveCount();

    // Combine all counts
    combineLatest([
      allCount$,
      pendingCount$,
      respondedCount$,
      archiveCount$,
    ]).subscribe(([allCount, pendingCount, respondedCount, archiveCount]) => {
      this.filterTabs = [
        { id: 'all', label: 'All', count: allCount },
        { id: 'pending', label: 'Pending', count: pendingCount },
        { id: 'responded', label: 'Responded', count: respondedCount },
        { id: 'archive', label: 'Archive', count: archiveCount },
      ];
    });
  }

  private getArchiveCount(): Observable<number> {
    return this.lettersService
      .getLettersWithFilters({
        filter: 'all',
        showDeleted: true,
        page: 1,
        perPage: 1,
      })
      .pipe(map((response) => response.total));
  }

  onRowClick(row: LetterData) {
    if (this.isManager) {
      // For managers, open PDF viewer instead of navigating to details
      this.openDocumentViewer(row);
    } else {
      // For non-managers, navigate to details page
      // Determine archived status based on current filter tab (archive list)
      const isArchived = this.activeFilterTab === 'archive';
      this.router.navigate(['/letters/details'], {
        queryParams: {
          id: row.id,
          archived: isArchived ? 'true' : undefined,
        },
      });
    }
  }

  onCommentClick(row: LetterData) {
    // TODO: Implement comment modal functionality
  }

  onReply(row: LetterData) {
    // Navigate to letters reply page with only ID parameter
    this.router.navigate(['/letters/reply'], {
      queryParams: {
        id: row.id,
      },
    });
  }

  onApprove(data: any): void {
    if (data.row && data.comment) {
      this.approveLetter(data.row.id, data.comment);
    }
  }

  onReject(data: any): void {
    if (data.row && data.comment) {
      this.rejectLetter(data.row.id, data.comment);
    }
  }

  onEdit(letter: LetterData): void {
    if (!letter.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Letter ID is missing',
      });
      return;
    }

    // Navigate to edit page with the letter ID
    this.router.navigate(['/letters/edit'], {
      queryParams: {
        id: letter.id,
      },
    });
  }

  /**
   * Approve a letter
   */
  private approveLetter(id: string, comment: string): void {
    this.lettersService.approveRejectLetter(id, true, comment).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Letter approved successfully',
        });
        // Refresh the data
        this.loadLettersData();
      },
      error: (error) => {
        const errorMessage = error.error?.message || 'Failed to approve letter';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
        });
      },
    });
  }

  /**
   * Reject a letter
   */
  private rejectLetter(id: string, comment: string): void {
    this.lettersService.approveRejectLetter(id, false, comment).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Letter rejected successfully',
        });
        // Refresh the data
        this.loadLettersData();
      },
      error: (error) => {
        const errorMessage = error.error?.message || 'Failed to reject letter';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
        });
      },
    });
  }

  onUploadDocument(): void {
    this.router.navigate(['/letters/upload']);
  }

  // Document viewer methods
  onDocumentNameClick(letter: LetterData): void {
    if (!letter.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No letter ID available for document download.',
      });
      return;
    }

    // Show modal immediately with loading state
    this.documentLoading = true;
    this.showDocumentViewer = true;
    this.currentDocument = null; // Clear previous document

    // Call the download API to get the document blob
    this.lettersService.downloadLetterDocument(letter.id).subscribe({
      next: (response: { blob: Blob; filename: string; type: string }) => {
        // Create a blob URL for viewing only after successful download
        const url = window.URL.createObjectURL(response.blob);

        // Create a document attachment object for the viewer
        this.currentDocument = {
          name: response.filename,
          size: this.formatFileSize(response.blob.size),
          type: response.type, // Use the MIME type from API response
          fileData: url, // Store the blob URL for viewing
          lastModified: Date.now(),
          blobUrl: url, // Store the blob URL separately for cleanup
          originalBlob: response.blob, // Store the original blob for download
        };

        // Hide loading state
        this.documentLoading = false;
      },
      error: (error) => {
        this.documentLoading = false;
        this.showDocumentViewer = false; // Hide modal on error

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load document. Please try again.',
        });
      },
    });
  }

  openDocumentViewer(row: LetterData): void {
    // Show toaster message instead of alert
    this.messageService.add({
      severity: 'warn',
      summary: 'Document Preview Not Available',
      detail:
        'Document preview access is  not available. Please contact administrator.',
      life: 5000,
    });
  }

  onDocumentViewerClose(): void {
    // Clean up blob URL to prevent memory leaks
    if (this.currentDocument?.blobUrl) {
      window.URL.revokeObjectURL(this.currentDocument.blobUrl);
    }
    this.showDocumentViewer = false;
    this.currentDocument = null;
  }

  onDocumentDownload(attachment: DocumentAttachment): void {
    // If we have the original blob from the API, use it directly
    if (attachment.originalBlob) {
      // Create download link
      const url = window.URL.createObjectURL(attachment.originalBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // this.messageService.add({
      //   severity: 'success',
      //   summary: 'Success',
      //   detail: 'Document downloaded successfully.'
      // });
    }
  }

  onDocumentOpenInNewTab(attachment: DocumentAttachment): void {
    // Open the document in a new tab using the blob URL
    if (attachment.blobUrl) {
      window.open(attachment.blobUrl, '_blank');
    }
  }

  /**
   * Format file size in human readable format
   * @param bytes File size in bytes
   * @returns Formatted file size string
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Audit log methods
  onAuditLog(letter: LetterData): void {
    if (!letter.id) {
      return;
    }

    // Set loading state and show modal
    this.auditLoading = true;
    this.selectedRow = letter;
    this.auditDialogVisible = true;

    // Load audit logs from API
    this.lettersService.getAuditLog(letter.id).subscribe({
      next: (auditLogs) => {
        // Transform the API response to match the expected AuditsData structure
        this.auditData = (auditLogs || []).map((log: any) => ({
          date: this.formatDateForAudit(log.createdOn),
          time: this.formatTimeForAudit(log.createdOn),
          user: log.userName,
          action: log.userAction,
        }));

        this.auditLoading = false;
      },
      error: (error) => {
        this.auditLoading = false;

        // Show error message to user
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load audit logs. Please try again.',
          life: 5000,
        });
      },
    });
  }

  onAuditClose(): void {
    this.auditDialogVisible = false;
    this.selectedRow = null;
    this.auditData = [];
    this.auditLoading = false;
  }

  formatDateForAudit(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  formatTimeForAudit(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
}
